import type { TipTapDoc } from "@compass/shared/schema/tiptapDoc";
import { getPlainTextFromDoc } from "@compass/shared/tiptap/plaintext";
import { TrashIcon } from "@heroicons/react/20/solid";
import * as Sentry from "@sentry/react";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { Avatar } from "components/elements";
import { FileAttachment, ProseDocument, Spinner } from "components/elements";
import { DeleteDialogModal } from "components/elements/DeleteDialogModal";
import { ThreadCommentForm } from "components/form/ThreadCommentForm";
import { useUserTimezone } from "hooks/useUserTimezone";
import { formatFullTimestamp, formatRelativeTime } from "lib/relativeTime";
import type { ApiThreadComment } from "lib/threadCommentUtils";
import { trpc } from "lib/trpc";

const PAGE_SIZE = 50;

interface ChatAreaProps {
    userId: string;
    conversationId: string;
    setSelectedConversation: (conversationId: string | null) => void;
}

export const ChatArea = ({ userId, conversationId, setSelectedConversation }: ChatAreaProps) => {
    const [messageToDelete, setMessageToDelete] = useState<string | null>(null);
    const timeZone = useUserTimezone();
    const utils = trpc.useContext();
    const deleteMessageMutation = trpc.threads.deleteComment.useMutation({
        onSuccess: () => {
            utils.messages.getConversationView.invalidate({ conversationId });
            utils.messages.getConversations.invalidate();
            setMessageToDelete(null);
        },
        onError: (e) => {
            console.error(e);
            enqueueSnackbar(e.message || "Failed to delete the message. Please try again.", {
                variant: "error",
            });
        },
    });

    const { data: view, isPending: isLoading } = trpc.messages.getConversationView.useQuery(
        { conversationId },
        { placeholderData: (prev) => prev },
    );

    const conversation = view?.conversation;
    const thread = view?.thread;
    const threadComments = view?.comments ?? [];

    // Older messages loaded by scrolling to the top of the transcript.
    // Stored oldest-first so we can prepend them directly to the live page.
    const [olderMessages, setOlderMessages] = useState<ApiThreadComment[]>([]);
    const [hasMoreOlder, setHasMoreOlder] = useState(true);
    const [isLoadingOlder, setIsLoadingOlder] = useState(false);

    // Reset pagination state whenever the open conversation changes.
    useEffect(() => {
        setOlderMessages([]);
        setHasMoreOlder(true);
        setIsLoadingOlder(false);
        loadingOlderRef.current = false;
    }, [conversationId]);

    // Seed `hasMoreOlder` from the initial composite endpoint's `hasMore`,
    // but only while we haven't paged anything in yet. Once buffered older
    // pages exist, `view.hasMore` is meaningless ("more older than the live
    // page's oldest") — those messages are already in `olderMessages`. The
    // load-older callback owns `hasMoreOlder` after that point.
    const initialHasMore = view?.hasMore;
    useEffect(() => {
        if (initialHasMore === undefined) return;
        if (olderMessages.length > 0) return;
        setHasMoreOlder(initialHasMore);
    }, [initialHasMore, olderMessages.length]);

    // SSE: invalidate on new message in this conversation
    trpc.realtime.onNewMessage.useSubscription(
        { conversationId },
        {
            enabled: !!conversationId,
            onData: () => {
                utils.messages.getConversationView.invalidate({ conversationId });
                utils.messages.getConversations.invalidate();
            },
        },
    );

    // Server (`messages.getConversationView`) returns comments oldest-first
    // already, so we just append the live page after any older buffered pages
    // — top of the list is the start of the conversation, bottom is the
    // latest message above the input.
    //
    // Dedupe by id on merge: when SSE invalidates the live page after the user
    // has paged up, the refetched live page can overlap with `olderMessages`.
    // First-write-wins keeps the older buffered copy, which preserves React
    // keys across the refresh.
    const allComments = useMemo(() => {
        const seen = new Set<number>();
        const out: ApiThreadComment[] = [];
        for (const c of olderMessages) {
            if (seen.has(c.id)) continue;
            seen.add(c.id);
            out.push(c);
        }
        for (const c of threadComments) {
            if (seen.has(c.id)) continue;
            seen.add(c.id);
            out.push(c);
        }
        return out;
    }, [olderMessages, threadComments]);

    const messages = allComments.map((comment) => ({
        id: comment.id.toString(),
        contentJson: comment.contentJson,
        senderId: comment.authorId.toString(),
        receiverId:
            conversation?.participants.find((p) => p.id !== comment.authorId.toString())?.id ?? "",
        createdAt: comment.createdAt,
        read: true, // Thread comments don't have read status yet, assume read
        attachments: comment.attachments.map((att) => ({
            id: att.id,
            name: att.name ?? "",
            displayName: att.displayName ?? att.name,
            description: att.description,
            sizeBytes: att.sizeBytes,
            kind: att.kind,
            type: att.type ?? att.kind,
            userId: att.userId,
            status: att.status,
            uploaded: att.uploaded,
        })),
    }));

    const sendMessage = trpc.threads.createComment.useMutation();
    const clearFormRef = useRef<(() => void) | null>(null);

    const handleSend = (data: {
        contentJson: TipTapDoc;
        attachmentIds: string[];
        isAnonymous: boolean;
    }) => {
        if (
            (getPlainTextFromDoc(data.contentJson).trim().length === 0 &&
                data.attachmentIds.length === 0) ||
            !conversation ||
            !thread
        )
            return;

        sendMessage.mutate(
            {
                threadId: thread.id,
                contentJson: data.contentJson,
                attachmentIds: data.attachmentIds,
            },
            {
                onSuccess: () => {
                    clearFormRef.current?.();
                    utils.messages.getConversationView.invalidate({ conversationId });
                    utils.messages.getConversations.invalidate();
                },
                onError: (error) => {
                    console.error("Error sending message:", error);
                },
            },
        );
    };

    // Refs for scroll behavior + cursor pagination
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const topSentinelRef = useRef<HTMLDivElement | null>(null);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const prevConversationIdRef = useRef(conversationId);
    const prevLatestMessageIdRef = useRef<string | undefined>(undefined);
    // Synchronous in-flight guard. `setIsLoadingOlder` is async, so the
    // IntersectionObserver can fire `loadOlder` again before React rerenders
    // and the `isLoadingOlder` state read sees the update. The ref blocks
    // overlapping fetches in the same tick.
    const loadingOlderRef = useRef(false);

    // Scroll to bottom only when the conversation changed or a NEW message
    // arrived at the end. Prepending older messages must NOT scroll the user.
    const latestMessageId = messages[messages.length - 1]?.id;
    useEffect(() => {
        const conversationChanged = prevConversationIdRef.current !== conversationId;
        const latestChanged = prevLatestMessageIdRef.current !== latestMessageId;
        prevConversationIdRef.current = conversationId;
        prevLatestMessageIdRef.current = latestMessageId;

        if ((conversationChanged || latestChanged) && messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: conversationChanged ? "auto" : "smooth",
            });
        }
    }, [conversationId, latestMessageId]);

    // Load the next page of older messages, preserving the user's scroll
    // position so the visible content doesn't jump.
    const oldestCursor = allComments[0]?.createdAt;
    const loadOlder = useCallback(async () => {
        if (!thread || !oldestCursor || !hasMoreOlder) return;
        if (loadingOlderRef.current) return;
        loadingOlderRef.current = true;

        // Snapshot the conversation we're paging in. If the user switches away
        // before the fetch resolves, drop the result — otherwise the new
        // conversation would inherit stale prepended messages.
        const fetchedForConversationId = conversationId;
        const scroller = scrollContainerRef.current;
        const oldScrollHeight = scroller?.scrollHeight ?? 0;
        const oldScrollTop = scroller?.scrollTop ?? 0;

        setIsLoadingOlder(true);
        try {
            const newest = await utils.threads.getComments.fetch({
                threadId: thread.id,
                sort: "desc",
                before: new Date(oldestCursor),
                limit: PAGE_SIZE,
            });
            if (fetchedForConversationId !== conversationId) return;
            if (newest.length === 0) {
                setHasMoreOlder(false);
                return;
            }
            // `sort: desc` returns newest-first; reverse to oldest-first before
            // prepending so the buffered list stays chronological.
            const olderAsc = [...newest].reverse();
            setOlderMessages((prev) => [...olderAsc, ...prev]);
            if (newest.length < PAGE_SIZE) setHasMoreOlder(false);

            // Restore scroll on the next paint. The container has grown by
            // (newScrollHeight - oldScrollHeight) above the previous content.
            requestAnimationFrame(() => {
                if (!scroller) return;
                scroller.scrollTop = scroller.scrollHeight - oldScrollHeight + oldScrollTop;
            });
        } catch (err) {
            Sentry.captureException(err, { tags: { feature: "chat-load-older" } });
        } finally {
            loadingOlderRef.current = false;
            if (fetchedForConversationId === conversationId) setIsLoadingOlder(false);
        }
    }, [thread, oldestCursor, hasMoreOlder, utils, conversationId]);

    // Trigger `loadOlder` when the top sentinel scrolls into view.
    useEffect(() => {
        const sentinel = topSentinelRef.current;
        const root = scrollContainerRef.current;
        if (!sentinel || !root || !hasMoreOlder) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    loadOlder();
                }
            },
            { root, rootMargin: "100px 0px 0px 0px" },
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loadOlder, hasMoreOlder]);

    if (isLoading) {
        return (
            <span className="flex min-h-screen items-center justify-center">
                <Spinner className="h-10 w-10 text-slate-500" />
            </span>
        );
    }

    if (!conversation) {
        return (
            <div className="flex items-center justify-center p-4 text-gray-500">
                No conversation found.
            </div>
        );
    }

    const participantName = conversation.participants
        .filter((participant) => participant.id !== userId)
        .map(
            (participant) =>
                (participant as any).name ?? `${participant.firstName} ${participant.lastName}`,
        )
        .join(", ");
    const participantInactive = conversation.participants
        .filter((participant) => participant.id !== userId)
        .some((participant) => participant.isSocialEnabled === false);

    return (
        <div className="flex h-full min-w-full flex-col rounded-2xl border border-[#d5d5d5] bg-white">
            {/* Chat Header */}
            <header className="flex items-center justify-between border-b border-[#d5d5d5] p-3">
                <div className="flex items-center space-x-4">
                    <div className="h-[46px] w-[46px] flex-shrink-0 overflow-hidden rounded-xl bg-gray-300">
                        {conversation.participants[0]?.avatar ? (
                            <Avatar
                                className="h-[46px] w-[46px]"
                                photoUrl={conversation.participants[0]?.avatar}
                                name={
                                    (conversation.participants[0] as any)?.name ??
                                    `${conversation.participants[0]?.firstName} ${conversation.participants[0]?.lastName}`
                                }
                            />
                        ) : (
                            <div className="flex h-full items-center justify-center bg-gray-400 text-white">
                                {conversation.participants[0]?.firstName?.[0]?.toUpperCase() || "U"}
                            </div>
                        )}
                    </div>
                    <div
                        className={`text-[19px] font-bold text-[#424242] ${participantInactive ? "italic" : ""}`}
                    >
                        {participantName}
                        {participantInactive && " (inactive)"}
                    </div>
                </div>
                <CloseIcon
                    className="h-[30px] w-[30px] cursor-pointer"
                    onClick={() => setSelectedConversation(null)}
                />
            </header>

            {/* Messages */}
            <div className="flex flex-1 flex-col justify-between overflow-y-auto p-4">
                <div ref={scrollContainerRef} className="no-scrollbar overflow-y-auto pr-2">
                    <div ref={topSentinelRef} />
                    {isLoadingOlder && (
                        <div className="flex justify-center py-2">
                            <Spinner className="h-4 w-4 text-slate-400" />
                        </div>
                    )}
                    {messages.map((msg) => (
                        <div
                            key={msg.id}
                            className={`flex min-h-[56px] items-center ${msg.senderId === userId ? "justify-end" : "justify-start"}`}
                        >
                            <div className="group relative mt-3">
                                {/* Trash icon — hover overlay, outside the bubble */}
                                {msg.senderId === userId && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setMessageToDelete(msg.id);
                                        }}
                                        disabled={deleteMessageMutation.isPending}
                                        className="absolute -left-9 top-1/3 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded opacity-0 transition-opacity duration-150 hover:bg-red-50 group-hover:opacity-100"
                                        title="Delete Message"
                                        aria-label="Delete message"
                                    >
                                        <TrashIcon className="h-4 w-4 text-red-400" />
                                    </button>
                                )}

                                <div
                                    className={`max-w-sm break-words rounded-lg border-[0.75px] border-[#C8C8C8] px-3 py-2.5 transition-all duration-200 ease-in-out ${
                                        msg.senderId === userId
                                            ? "bg-[#0096B8] !text-white"
                                            : "bg-[#f7f7f7] !text-gray-800"
                                    }`}
                                >
                                    <ProseDocument
                                        doc={msg.contentJson}
                                        className={
                                            msg.senderId === userId
                                                ? "!text-white"
                                                : "text-[#424242]"
                                        }
                                    />
                                    {msg.attachments && msg.attachments.length > 0 && (
                                        <div className="mt-2 flex flex-col gap-2">
                                            {msg.attachments.map((file) => (
                                                <FileAttachment
                                                    key={file.id}
                                                    file={file}
                                                    className="bg-inherit"
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div
                                    className={`mt-1 text-xs text-gray-500 ${msg.senderId === userId ? "text-right" : "text-left"}`}
                                >
                                    <span title={formatFullTimestamp(msg.createdAt, timeZone)}>
                                        {formatRelativeTime(msg.createdAt, timeZone)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}

                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input */}
            <div className="border-[#424242]/8 rounded-b-2xl border-t bg-white p-4">
                <ThreadCommentForm
                    context="discussion"
                    onSubmit={handleSend}
                    isLoading={sendMessage.isPending}
                    placeholder="Type your message..."
                    submitLabel="Send"
                    componentType="message"
                    clearFormRef={clearFormRef}
                    editorFeatures={{
                        bold: true,
                        italic: true,
                        underline: true,
                        strike: true,
                        heading: true,
                        blockquote: true,
                        bulletList: true,
                        orderedList: true,
                        indent: true,
                        redoUndo: true,
                        link: true,
                    }}
                    contentHeightClassName="min-h-[40px]"
                />
            </div>

            <DeleteDialogModal
                open={messageToDelete !== null}
                setOpen={(isOpen) => {
                    if (!isOpen) setMessageToDelete(null);
                }}
                handleDelete={() => {
                    if (messageToDelete) {
                        deleteMessageMutation.mutate({ commentId: parseInt(messageToDelete, 10) });
                    }
                }}
                disabled={deleteMessageMutation.isPending}
                message="This action cannot be undone. This will permanently delete this message."
            />
        </div>
    );
};
